import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Image } from 'react-native';
import { TabType } from '../types';
import { styles } from '../styles';
import { themes, useTheme } from '../theme';
import logoImage from '../../assets/logo_komplett.png';

interface SidebarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  appMode: 'pvs' | 'robot';
  onAppModeChange: (m: 'pvs' | 'robot') => void;
}

const TIM_VISIBLE_KEY = 'pvs_tim_visible';

const tabsForMode = (mode: 'pvs' | 'robot'): { key: TabType; label: string; icon: string }[] =>
  mode === 'pvs'
    ? [
        { key: 'calendar', label: 'Terminkalender', icon: '📅' },
        { key: 'tasks', label: 'Aufgaben', icon: '📋' },
        { key: 'patients', label: 'Patienten', icon: '👥' },
        { key: 'messages', label: 'TI-Messenger', icon: '💬' },
      ]
    : [
        { key: 'calendar', label: 'Geräte', icon: '🩻' },
        { key: 'tasks', label: 'Aufgaben', icon: '📋' },
        { key: 'patients', label: 'Techniker', icon: '🔧' },
        { key: 'messages', label: 'Messenger', icon: '💬' },
      ];

export function Sidebar({ activeTab, onTabChange, appMode, onAppModeChange }: SidebarProps) {
  const allTabs = tabsForMode(appMode);
  const { theme, setTheme } = useTheme();
  const [timVisible, setTimVisible] = useState<boolean>(() => {
    try {
      const v = window.localStorage.getItem(TIM_VISIBLE_KEY);
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(TIM_VISIBLE_KEY, String(timVisible));
    } catch {}
  }, [timVisible]);

  useEffect(() => {
    if (!timVisible && activeTab === 'messages') onTabChange('calendar');
  }, [timVisible, activeTab, onTabChange]);

  const tabs = timVisible || appMode === 'robot' ? allTabs : allTabs.filter(t => t.key !== 'messages');

  return (
    <View style={[styles.sidebar, { backgroundColor: theme.sidebarBg }]}>
      <View style={[styles.sidebarHeader, { borderBottomColor: theme.sidebarBorder }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingHorizontal: 4, gap: 12 }}>
          <Text style={{ color: '#ccc', fontSize: 12, marginRight: 12 }}>PVS / Geräte</Text>
          <TouchableOpacity
            onPress={() => onAppModeChange(appMode === 'robot' ? 'pvs' : 'robot')}
            style={{
              width: 38,
              height: 22,
              borderRadius: 11,
              backgroundColor: appMode === 'robot' ? theme.pvs : '#555',
              justifyContent: 'center',
              padding: 2,
            }}
            accessibilityLabel="Roboterverwaltung"
          >
            <View
              style={{
                width: 18,
                height: 18,
                borderRadius: 9,
                backgroundColor: '#fff',
                alignSelf: appMode === 'robot' ? 'flex-end' : 'flex-start',
              }}
            />
          </TouchableOpacity>
        </View>
        {appMode === 'pvs' && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingHorizontal: 4, gap: 12 }}>
          <Text style={{ color: '#ccc', fontSize: 12, marginRight: 12 }}>TI-Messenger</Text>
          <TouchableOpacity
            onPress={() => setTimVisible(v => !v)}
            style={{
              width: 38,
              height: 22,
              borderRadius: 11,
              backgroundColor: timVisible ? theme.pvs : '#555',
              justifyContent: 'center',
              padding: 2,
            }}
            accessibilityLabel="TI-Messenger anzeigen"
          >
            <View
              style={{
                width: 18,
                height: 18,
                borderRadius: 9,
                backgroundColor: '#fff',
                alignSelf: timVisible ? 'flex-end' : 'flex-start',
              }}
            />
          </TouchableOpacity>
        </View>
        )}
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 10, marginBottom: 14 }}>
          {themes.map(t => {
            const selected = t.key === theme.key;
            return (
              <TouchableOpacity
                key={t.key}
                onPress={() => setTheme(t.key)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  borderWidth: selected ? 3 : 1,
                  borderColor: selected ? '#fff' : '#555',
                  overflow: 'hidden',
                  // @ts-ignore — RN-web passes through CSS
                  backgroundImage: `conic-gradient(${t.pvs} 0deg 120deg, ${t.timPrimary} 120deg 240deg, ${t.timSecondary} 240deg 360deg)`,
                }}
                accessibilityLabel={t.label}
              />
            );
          })}
        </View>
        <Image source={logoImage} style={styles.logoImage} />
        <Text style={styles.sidebarLogo}>
          {appMode === 'robot' ? 'Geräte-Leitstand' : 'PVS Praxisverwaltung'}
        </Text>
      </View>
      <View style={styles.sidebarNav}>
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.navItem,
              activeTab === tab.key && { backgroundColor: theme.sidebarActiveBg },
            ]}
            onPress={() => onTabChange(tab.key)}
          >
            <Text style={styles.navIcon}>{tab.icon}</Text>
            <Text style={[styles.navLabel, activeTab === tab.key && styles.navLabelActive]}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={[styles.sidebarFooter, { borderTopColor: theme.sidebarBorder }]}>
        <Text style={styles.footerText}>Prof. Dr. Kybernetiker</Text>
      </View>
    </View>
  );
}
